import { useState } from "react";
import { mockReviews } from "@/mocks/orders";

export default function ProductReviews() {
  const [helpful, setHelpful] = useState<string[]>([]);

  const toggleHelpful = (id: string) => {
    setHelpful((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const avgRating = 4.8;
  const ratingBreakdown = [
    { stars: 5, pct: 72 },
    { stars: 4, pct: 18 },
    { stars: 3, pct: 6 },
    { stars: 2, pct: 2 },
    { stars: 1, pct: 2 },
  ];

  return (
    <div>
      <h2 className="text-lg font-extrabold text-gray-900 mb-6">Customer Reviews</h2>

      {/* Rating Summary */}
      <div className="flex flex-col sm:flex-row gap-6 mb-8 p-5 bg-gray-50 rounded-2xl">
        <div className="text-center flex-shrink-0">
          <div className="text-5xl font-extrabold text-gray-900">{avgRating}</div>
          <div className="flex justify-center gap-0.5 my-1">
            {[1,2,3,4,5].map((s) => (
              <i key={s} className={`text-sm ${s <= 4 ? "ri-star-fill text-yellow-400" : "ri-star-half-fill text-yellow-400"}`} />
            ))}
          </div>
          <div className="text-xs text-gray-400">2,341 reviews</div>
        </div>
        <div className="flex-1 space-y-1.5">
          {ratingBreakdown.map((r) => (
            <div key={r.stars} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8 text-right">{r.stars}★</span>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${r.pct}%` }} />
              </div>
              <span className="text-xs text-gray-400 w-8">{r.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Review List */}
      <div className="space-y-5">
        {mockReviews.map((review) => (
          <div key={review.id} className="border border-gray-100 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <img src={review.avatar} alt={review.user} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{review.user}</span>
                  {review.verified && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                      <i className="ri-shield-check-fill mr-0.5" />Verified Purchase
                    </span>
                  )}
                  {review.referredBy && (
                    <span className="text-xs text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full font-medium">
                      via {review.referredBy}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className={`text-xs ${s <= review.rating ? "ri-star-fill text-yellow-400" : "ri-star-line text-gray-300"}`} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400">{review.date}</span>
                </div>
              </div>
            </div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">{review.title}</h4>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">{review.body}</p>
            {review.images.length > 0 && (
              <div className="flex gap-2 mb-3">
                {review.images.map((img, i) => (
                  <div key={i} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-100">
                    <img src={img} alt="Review" className="w-full h-full object-cover object-top" />
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => toggleHelpful(review.id)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${helpful.includes(review.id) ? "border-orange-300 text-orange-600 bg-orange-50" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >
              <i className={helpful.includes(review.id) ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
              Helpful ({review.helpful + (helpful.includes(review.id) ? 1 : 0)})
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
